import React, { useEffect, useLayoutEffect, useRef, useCallback, useState } from "react";
import ReactDOM from "react-dom";
import Picker from "@emoji-mart/react";
import { EMOJI_NO_COUNTRY_FLAGS } from "../utils/emojiPicker";
import data from "@emoji-mart/data";
import i18nDe from "@emoji-mart/data/i18n/de.json";
import MaterialIcon from "./MaterialIcon";
import { emojiUnicode } from "../utils/encryptDecryptFunction";
import "./PreloadedEmojiPicker.css";

// Quick reactions of the mobile app's message menu (v2/pro/proChatKit PRO_REACTIONS), same order.
const DEFAULT_QUICK_REACTIONS = ["👍", "✅", "👀", "🙏", "💪", "❤️"];

// Local storage key for emoji usage tracking
const EMOJI_USAGE_KEY = "kjuup_emoji_usage";

const MARGIN = 8;
const GAP = 6;
const MIN_READY_HEIGHT = 120;

/**
 * Get emoji usage from localStorage
 */
const getEmojiUsage = () => {
  try {
    const stored = localStorage.getItem(EMOJI_USAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Save emoji usage to localStorage
 */
const saveEmojiUsage = (emoji) => {
  try {
    const usage = getEmojiUsage();
    usage[emoji] = (usage[emoji] || 0) + 1;
    localStorage.setItem(EMOJI_USAGE_KEY, JSON.stringify(usage));
  } catch {
    // Ignore storage errors
  }
};

const toHex = (glyph) => {
  try {
    return emojiUnicode(glyph).toLowerCase();
  } catch (_) {
    return "";
  }
};

/**
 * PreloadedEmojiPicker - reaction bar of the bubble's hover bar.
 * Opens right above the hover bar (same start edge, so it reads as the bar growing upwards);
 * "more" swaps the row for the full picker at the same spot. The quick set is the fixed mobile
 * set (the same on every device), so a reaction looks and sits the same in the app and on the web.
 */
const PreloadedEmojiPicker = ({
  isOpen,
  onClose,
  onEmojiClick,
  onReactionClick,
  triggerElement,
  isGerm = false,
  internal = false,
  myReactions = [],
  labels = {},
}) => {
  const pickerRef = useRef(null);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [position, setPosition] = useState(null); // null until measured

  // Reset state when picker opens/closes
  useEffect(() => {
    if (isOpen) {
      setShowFullPicker(false);
    } else {
      setPosition(null);
    }
  }, [isOpen]);

  // Place from the real size: above the hover bar, aligned to its start edge (right edge for
  // own messages), flipped below when there is no room above, clamped to the viewport.
  const place = useCallback(() => {
    const el = pickerRef.current;
    if (!el || !triggerElement) return;
    const bar = triggerElement.closest(".contextMenu");
    const r = (bar || triggerElement).getBoundingClientRect();
    const alignEnd = !!(bar && bar.classList.contains("right-context"));
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (showFullPicker && h < MIN_READY_HEIGHT) return; // full picker not drawn yet
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = alignEnd ? r.right - w : r.left;
    let top = r.top - h - GAP;
    if (top < MARGIN) top = r.bottom + GAP;

    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
    setPosition({ top, left });
  }, [triggerElement, showFullPicker]);

  // The full picker (em-emoji-picker) renders asynchronously: re-place once its real size lands.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    place();
    const el = pickerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => place());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, showFullPicker, place]);

  // Escape closes; scrolling the chat or resizing closes (the bar it hangs off moves away).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const onScroll = (e) => {
      if (pickerRef.current && e.target && pickerRef.current.contains(e.target)) return;
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [isOpen, onClose]);

  // Click outside closes (the trigger itself is left alone so it does not close-and-reopen).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (pickerRef.current && pickerRef.current.contains(e.target)) return;
      if (triggerElement && triggerElement.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, triggerElement]);

  // Handle emoji selection (quick reaction or full picker)
  const handleEmojiSelect = useCallback(
    (emojiData, isQuickReaction = false) => {
      const emoji = isQuickReaction
        ? emojiData
        : emojiData?.native || emojiData?.emoji;

      if (!emoji) return;

      // Track usage
      saveEmojiUsage(emoji);

      const formattedData = {
        emoji: emoji,
        native: emoji,
        ...(emojiData?.unified && { unified: emojiData.unified }),
        ...(emojiData?.id && { id: emojiData.id }),
      };

      if (onReactionClick) {
        onReactionClick(formattedData);
      } else if (onEmojiClick) {
        onEmojiClick(formattedData);
      }
    },
    [onEmojiClick, onReactionClick]
  );

  if (!isOpen) return null;

  const mine = new Set((myReactions || []).map((h) => String(h).toLowerCase()));
  const moreLabel = labels.moreReactions || (isGerm ? "Weitere Reaktionen" : "More reactions");

  // Use Portal to render outside any stacking context
  return ReactDOM.createPortal(
    <div
      ref={pickerRef}
      className={`preloaded-emoji-picker preloaded-emoji-picker--fixed${showFullPicker ? " preloaded-emoji-picker-expanded" : ""}${internal ? " kjc-internal" : ""}${position ? "" : " is-measuring"}`}
      style={position ? { top: position.top, left: position.left } : undefined}
      role="dialog"
      aria-label={labels.addReaction || (isGerm ? "Reaktion hinzufügen" : "Add reaction")}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {!showFullPicker ? (
        <div className="kj-react-bar">
          {DEFAULT_QUICK_REACTIONS.map((emoji) => {
            const on = mine.has(toHex(emoji));
            return (
              <button
                key={emoji}
                type="button"
                className={`kj-react-bar__btn${on ? " is-on" : ""}`}
                onClick={() => handleEmojiSelect(emoji, true)}
                aria-pressed={on}
              >
                {emoji}
              </button>
            );
          })}
          <span className="kj-react-bar__divider" aria-hidden="true" />
          <button
            type="button"
            className="kj-react-bar__btn kj-react-bar__btn--more"
            onClick={() => {
              setPosition(null); // hide until the full picker has its size
              setShowFullPicker(true);
            }}
            title={moreLabel}
            aria-label={moreLabel}
            aria-expanded={false}
          >
            <MaterialIcon icon="add_reaction" size={22} weight={300} />
          </button>
        </div>
      ) : (
        <div className="full-picker-container">
          <Picker
            data={data}
            i18n={isGerm ? i18nDe : undefined}
            onEmojiSelect={(emoji) => handleEmojiSelect(emoji, false)}
            theme="light"
            autoFocus
            previewPosition="none"
            skinTonePosition="none"
            searchPosition="sticky"
            navPosition="top"
            perLine={8}
            maxFrequentRows={2}
            emojiSize={24}
            emojiButtonSize={38}
            set="native"
            noCountryFlags={EMOJI_NO_COUNTRY_FLAGS}
          />
        </div>
      )}
    </div>,
    document.body
  );
};

export default PreloadedEmojiPicker;
